import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import Tab from './Tab';
import NavigationIcon from './NavigationIcon';

const ChildLink = ({ item }) => (
  <Link href={item.disabled ? {} : item.href}>
    <li
      className={`pl-11 pr-4 py-1 xl:py-1.5 flex items-center rounded cursor-pointer transition-all ease-in-out duration-200 ${
        item.active ? 'bg-primary-900 bg-opacity-10' : 'hover:bg-[#1756841a]'
      }`}
    >
      <span
        className={`text-[13px] ${
          item.active ? 'text-primary-900 font-medium' : 'text-darkGrey-400'
        }`}
      >
        {item.name}
      </span>
    </li>
  </Link>
);

ChildLink.propTypes = {
  item: PropTypes.shape({
    name: PropTypes.string,
    href: PropTypes.string,
    active: PropTypes.bool,
    disabled: PropTypes.bool,
  }).isRequired,
};

const NavGroup = ({ item }) => {
  const anyChildActive = (item.children || []).some((child) => child.active);
  const [expanded, setExpanded] = useState(item.active || anyChildActive);

  return (
    <li className="list-none">
      <div
        className={`flex items-center group relative rounded transition-all ease-in-out duration-200 ${
          item.active && !anyChildActive ? 'bg-primary-900 bg-opacity-10' : ''
        }`}
      >
        <Link href={item.href}>
          <div className="flex-1 flex items-center gap-x-2.5 px-3 py-1 xl:px-4 xl:py-1.5 cursor-pointer">
            <NavigationIcon
              iconKey={item.iconKey}
              isActive={item.active}
              size={16}
              className="w-[14px] h-[14px] xl:w-4 xl:h-4 2xl:w-6 2xl:h-6"
            />
            <span
              className={`pt-0.5 text-sm ${
                item.active ? 'text-primary-900 font-medium' : 'text-darkGrey-400 group-hover:text-primary-900'
              }`}
            >
              {item.name}
            </span>
          </div>
        </Link>
        <button
          type="button"
          aria-label={expanded ? `Collapse ${item.name}` : `Expand ${item.name}`}
          onClick={() => setExpanded((prev) => !prev)}
          className="px-2 py-2 text-darkGrey-400 hover:text-primary-900"
        >
          <svg
            className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
      {expanded && (
        <ul className="mt-1 space-y-1">
          {item.children.map((child) => (
            <ChildLink key={child.href} item={child} />
          ))}
        </ul>
      )}
    </li>
  );
};

NavGroup.propTypes = {
  item: PropTypes.shape({
    name: PropTypes.string,
    href: PropTypes.string,
    iconKey: PropTypes.string,
    active: PropTypes.bool,
    children: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
};

const NavigationMenuList = ({ menuItems = [], listMainClass }) => (
  <ul className={`space-y-2 ${listMainClass}`}>
    {menuItems.map((item, index) => (
      item.children && item.children.length > 0 ? (
        <NavGroup key={item.href || index} item={item} />
      ) : (
        <Tab
          iconKey={item.iconKey}
          listItemClass=""
          listName={item.name}
          href={item.href}
          key={item.href || index}
          active={item.active}
          disabled={item.disabled}
          toolTip={item.toolTip}
          toolTipText={item.toolTipText}
        />
      )
    ))}
  </ul>
);

NavigationMenuList.propTypes = {
  className: PropTypes.string,
  listMainClass: PropTypes.string,
  listItemClass: PropTypes.string,
  menuItems: PropTypes.array,
};

export default NavigationMenuList;
